import keycloak, { FORCE_TOKEN_REFRESH } from '@/keycloak';
import { redirectToLogin } from '@/auth/redirectToLogin';
import { AuthState, selectTokenRejected, tokenAccepted, tokenRejected } from '@/store/auth';
import {
    createApi,
    fetchBaseQuery,
    BaseQueryApi,
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import { definitions, operations } from '@/apitypes';
import {
    CommentType,
    CompanyComment,
    CreateCompanyComment,
    SyncMailchimpPutQuery,
    PostIncentive,
    PostIncentiveResponse,
    GetIncentivesResponse,
    GetIncentivesQuery,
    GetLicenseTypesQuery,
    GetLicenseTypesResponse,
    EndCompanyLicensesPayload,
    ActionsResponse,
    GetVossInvoiceBalanceQuery,
    GetVossUsageAuditQuery,
    VossUsageAuditResponse,
    GetVossLicenseAuditRunsQuery,
    VossLicenseAuditRunsResponse,
    GetVossLicenseAuditFindingsQuery,
    VossLicenseAuditFindingsResponse,
    StartVossLicenseAuditPreviewPayload,
    StartVossLicenseAuditPreviewResponse,
    CancelVossLicenseAuditRunResponse,
    ProductType,
    PostDiscountAgreement,
    Subscription,
    GetVossSingleProductGroupQuery,
    GetSingleVossProductGroupResponse,
    GetVossProductsResponse,
    GetVossProductsQuery,
    PutDiscontAgreementResponse,
    DiscountAgreement,
    DiscountAgreementListItem,
    MailchimpSyncExecutionsQuery,
    MailchimpSyncExecution,
    Ticket,
    TicketsResponse,
    GetTicketsQuery,
    CreateTicket,
    UpdateTicket,
    DeleteTicketParams,
    TicketStatusesResponse,
    TicketTypesResponse,
    TicketAreasResponse,
    TicketComment,
    TicketCommentsResponse,
    CreateTicketComment,
    DeleteTicketCommentParams,
    CustomersResponse,
    GetCustomersQuery,
    DeleteCustomerParams,
    DeletedCompanyResponse,
    CompanyDeletionSchedulesResponse,
    CompanyDeletionScheduleQuery,
    CompanyDeletionScheduleResponse,
    ScheduleCompanyDeletionPayload,
    CancelCompanyDeletionPayload,
    ApproveCompanyPayload,
    ApproveCompanyResponse,
    NewsletterBroadcastPayload,
    NewsletterBroadcastResponse,
    TestNewsletterBroadcastPayload,
    FeatureFlagAdminResponse,
    GetFeatureFlagsResponse,
    GetFeatureFlagsQuery,
    GetAssistantUsageQuery,
    GetAssistantUsageResponse,
    CreateFeatureFlagBody,
    UpdateFeatureFlagBody,
    DeleteFeatureFlagParams,
    CompanyFeatureFlagResponse,
    GetCompanyFeatureFlagsResponse,
    SetCompanyFeatureFlagBody,
    BulkSetCompanyFeatureFlagBody,
    BulkSetCompanyFeatureFlagResponse,
    WidgetConfigurationSchemaQuery,
    GetWidgetConfigurationSchemasResponse,
    WidgetConfigurationSchemaResponse,
    CreateWidgetConfigurationSchemaBody,
    GetWidgetConfigurationsQuery,
    GetWidgetConfigurationsResponse,
    WidgetConfigurationResponse,
    WidgetConfigurationByIdParams,
    CreateWidgetConfigurationBody,
    UpdateWidgetConfigurationBody,
    DeleteWidgetConfigurationParams,
    ResetWidgetConfigurationParams,
    BumpWidgetConfigurationVersionBody,
    SetWidgetConfigurationSchemaMigrationBody,
    DeleteWidgetConfigurationSchemaMigrationParams,
    ResetDemoAccountsResponse,
    AllowedRedirectDomainResponse,
    GetRedirectDomainsResponse,
    GetRedirectDomainsQuery,
    CreateRedirectDomainBody,
    DeleteRedirectDomainParams,
    BillingInformation,
    UpdateBillingInformationPayload,
    BillingMethodOption,
    BillingCountryOption,
    BillingLicensePlanOption,
    QueryResults,
    CompanyServicesResponse,
    GetCompanyServicesQuery,
    CompanyServicePricesResponse,
    GetCompanyServicePricesQuery,
    CompanyArticlesResponse,
    GetCompanyArticlesQuery,
    ArticleTypesResponse,
} from '@/types/types';
import { defaultSyncMailchimpOptions } from './mailchimp';

const rawBaseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-api-key': process.env.REACT_APP_API_KEY,
        'x-language': 'sv',
    },
    prepareHeaders(headers) {
        const bearerToken = keycloak?.token;
        const sessionId = keycloak?.sessionId;

        if (bearerToken && sessionId) {
            headers.set('Authorization', `Bearer ${bearerToken}`);
            headers.set('X-Ss-Id', `${sessionId}`);
        }

        return headers;
    },
});

type RawQueryResult = Awaited<ReturnType<typeof rawBaseQuery>>;

const isTokenRejected = (api: BaseQueryApi) =>
    selectTokenRejected(api.getState() as { auth: AuthState });

const forceRefreshToken = async (): Promise<boolean> => {
    try {
        await keycloak.updateToken(FORCE_TOKEN_REFRESH);
        return true;
    } catch {
        return false;
    }
};

const clearRejection = (api: BaseQueryApi) => {
    if (isTokenRejected(api)) api.dispatch(tokenAccepted());
};

const handleRefreshFailure = (api: BaseQueryApi) => {
    const isRedirecting = redirectToLogin();
    if (!isRedirecting) api.dispatch(tokenRejected());
};

const refreshAndRetry = async (
    args: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: {},
    firstResult: RawQueryResult
): Promise<RawQueryResult> => {
    const hasRefreshed = await forceRefreshToken();
    if (!hasRefreshed) {
        handleRefreshFailure(api);
        return firstResult;
    }

    const retryResult = await rawBaseQuery(args, api, extraOptions);
    if (retryResult.error?.status === 401) api.dispatch(tokenRejected());
    return retryResult;
};

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    const result = await rawBaseQuery(args, api, extraOptions);
    if (result.error?.status !== 401) {
        clearRejection(api);
        return result;
    }
    if (isTokenRejected(api)) return result;
    return refreshAndRetry(args, api, extraOptions, result);
};

export const apiService = createApi({
    reducerPath: 'bmApi',
    tagTypes: [
        'company',
        'companyComments',
        'companyApiKey',
        'incentives',
        'actions',
        'vossUsage',
        'vossBalance',
        'discountAgreements',
        'vossInvoice',
        'vossSubscriptions',
        'vossSubscriptionDiscounts',
        'vossSubscriptionLicenses',
        'mailchimpSyncExecutions',
        'widgetConfigurationSchemas',
        'widgetConfigurations',
        'featureFlags',
        'companyFeatureFlags',
        'tickets',
        'ticketComments',
        'ticketLookups',
        'customers',
        'vossErrorLogs',
        'vossDeadLetters',
        'vossUsageAudit',
        'vossLicenseAuditRuns',
        'vossLicenseAuditFindings',
        'redirectDomains',
        'assistantUsage',
        'billingInformation',
        'companyServices',
        'companyServicePrices',
        'companyArticles',
        'companyDeletionSchedules',
    ],
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => {
        return {
            getCompany: builder.query<
                operations['CompanyQuery_Get']['responses']['200']['schema']['Results'][0],
                definitions['CompanyQuery']
            >({
                transformResponse: (
                    response: operations['CompanyQuery_Get']['responses']['200']['schema']
                ) => {
                    return response.Results[0];
                },
                query: (params) => ({
                    url: '/superadmin/companies',
                    method: 'get',
                    params: {
                        ...params,
                        Take: 1,
                    },
                }),
                providesTags: ['company'],
            }),
            getCompanies: builder.query<
                operations['CompanyQuery_Get']['responses']['200']['schema'],
                definitions['CompanyQuery']
            >({
                query: (params) => ({
                    url: '/superadmin/companies',
                    method: 'get',
                    params: {
                        Active: true,
                        IncludeDeletionSchedule: true,
                        ...params,
                    },
                }),
                providesTags: ['company'],
            }),
            getCompanyOwners: builder.query<any, any>({
                query: (params) => ({
                    url: '/companyOwners',
                    method: 'get',
                    params: {
                        ...params,
                    },
                }),
            }),
            putCompany: builder.mutation<
                definitions['CompanyQueryResponse'],
                definitions['UpdateCompany'] & { CompanyId: string }
            >({
                query: ({ CompanyId, ...payload }) => ({
                    url: `/companies/${CompanyId}`,
                    method: 'put',
                    body: { CompanyId, ...payload },
                }),
                invalidatesTags: ['company'],
            }),
            getCompanyTypes: builder.query<
                definitions['QueryResponse_CompanyTypeQueryResponse_'],
                void
            >({
                query: () => ({
                    url: '/companies/types',
                    method: 'get',
                }),
            }),
            getCompanyCategories: builder.query<
                { Results?: definitions['CompanyCategoryQueryResponse'][] },
                void
            >({
                query: () => ({
                    url: '/categories',
                    method: 'get',
                    params: { Take: 1000 },
                }),
            }),
            getCountries: builder.query<
                { Results?: definitions['CountryQueryResponse'][] },
                void
            >({
                query: () => ({
                    url: '/countries',
                    method: 'get',
                }),
            }),
            getCompanyBookings: builder.query<any, any>({
                query: (params) => ({
                    url: '/superadmin/bookings',
                    method: 'get',
                    params: {
                        CompanyBookings: true,
                        IncludeServiceInformation: true,
                        Take: 50,
                        ...params,
                    },
                }),
                providesTags: ['company'],
            }),
            getCompanyAdministrators: builder.query<any, any>({
                query: (params) => ({
                    url: '/superadmin/administrators',
                    method: 'get',
                    params: {
                        Take: 50,
                        ...params,
                    },
                }),
                providesTags: ['company'],
            }),
            getCompanyLicenses: builder.query<any, any>({
                query: (params) => ({
                    url: '/superadmin/licenses/company',
                    method: 'get',
                    params: {
                        Take: 50,
                        ...params,
                    },
                }),
            }),
            endCompanyLicenses: builder.mutation<
                definitions['CompanyLicenseQueryResponse'],
                EndCompanyLicensesPayload
            >({
                query: (payload) => ({
                    url: '/superadmin/licenses/company/end',
                    method: 'post',
                    body: payload,
                }),
                invalidatesTags: ['company'],
            }),
            sendNewsletterBroadcast: builder.mutation<
                NewsletterBroadcastResponse,
                NewsletterBroadcastPayload
            >({
                query: (payload) => ({
                    url: '/superadmin/newsletters/broadcast',
                    method: 'post',
                    body: payload,
                }),
            }),
            testSendNewsletterBroadcast: builder.mutation<void, TestNewsletterBroadcastPayload>({
                query: (payload) => ({
                    url: '/superadmin/newsletters/broadcast/test',
                    method: 'post',
                    body: payload,
                }),
            }),
            getLicenseTypes: builder.query<GetLicenseTypesResponse, GetLicenseTypesQuery>({
                query: (params) => ({
                    url: '/licenses/types/',
                    method: 'get',
                    params: {
                        ...params,
                    },
                }),
            }),
            getCompanyInvoices: builder.query<any, any>({
                query: (params) => ({
                    url: '/superadmin/billing/company/invoices',
                    method: 'get',
                    params: {
                        Take: 50,
                        ...params,
                    },
                }),
            }),
            deleteCompany: builder.mutation<
                DeletedCompanyResponse,
                {
                    companyId: string;
                    gotApprovedByAdmin?: boolean;
                    force?: boolean;
                    reason?: string;
                }
            >({
                query: ({ companyId, gotApprovedByAdmin, force = false, reason }) => ({
                    url: `/superadmin/company/${companyId}`,
                    method: 'delete',
                    params: {
                        CompanyId: companyId,
                        GotApprovedByAdmin: !!gotApprovedByAdmin,
                        Force: force,
                        ...(reason ? { Reason: reason } : {}),
                    },
                }),
                invalidatesTags: ['companyDeletionSchedules'],
            }),
            getCompanyDeletionSchedules: builder.query<
                CompanyDeletionSchedulesResponse,
                CompanyDeletionScheduleQuery
            >({
                query: (params) => ({
                    url: '/superadmin/company/deletion-schedule',
                    method: 'get',
                    params: {
                        OrderByDesc: 'CreatedDate',
                        ...params,
                    },
                }),
                providesTags: ['companyDeletionSchedules'],
            }),
            scheduleCompanyDeletion: builder.mutation<
                CompanyDeletionScheduleResponse,
                ScheduleCompanyDeletionPayload
            >({
                query: ({ CompanyId, ...body }) => ({
                    url: `/superadmin/company/${CompanyId}/deletion-schedule`,
                    method: 'post',
                    body: { CompanyId, ...body },
                }),
                invalidatesTags: ['companyDeletionSchedules', 'company', 'companyComments'],
            }),
            cancelCompanyDeletionSchedule: builder.mutation<
                CompanyDeletionScheduleResponse,
                CancelCompanyDeletionPayload
            >({
                query: ({ CompanyId, CancelReason }) => ({
                    url: `/superadmin/company/${CompanyId}/deletion-schedule`,
                    method: 'delete',
                    body: { CompanyId, CancelReason },
                }),
                invalidatesTags: ['companyDeletionSchedules', 'company', 'companyComments'],
            }),
            approveCompany: builder.mutation<ApproveCompanyResponse, ApproveCompanyPayload>({
                query: ({ CompanyId, ...body }) => ({
                    url: `/superadmin/company/${CompanyId}/approve`,
                    method: 'post',
                    body: { CompanyId, ...body },
                }),
                invalidatesTags: ['company', 'companyComments', 'companyDeletionSchedules'],
            }),
            resetDemoAccounts: builder.mutation<ResetDemoAccountsResponse, void>({
                query: () => ({
                    url: '/superadmin/demoaccounts/reset',
                    method: 'post',
                }),
            }),
            syncMailchimpUsers: builder.mutation<any, SyncMailchimpPutQuery>({
                query: (data: SyncMailchimpPutQuery) => {
                    const body = {
                        deleteNonExisting:
                            data.DeleteNonExisting ?? defaultSyncMailchimpOptions.DeleteNonExisting ?? false,
                        test: data.Test ?? defaultSyncMailchimpOptions.Test ?? false,
                        manual: false,
                        memberCompanyId: null,
                    };

                    return {
                        url: '/superadmin/mailchimp/sync-all',
                        method: 'put',
                        body,
                    };
                },
            }),
            getCompanyComments: builder.query<CompanyComment[], any>({
                query: ({ companyId }) => {
                    return {
                        url: `/superadmin/company/${companyId}/comments`,
                        method: 'get',
                        params: {
                            OrderByDesc: 'Created',
                        },
                    };
                },
                providesTags: ['companyComments'],
            }),
            getCommentTypes: builder.query<CommentType[], void>({
                query: () => {
                    return {
                        url: `/superadmin/commentstype`,
                        method: 'get',
                    };
                },
            }),
            postCompanyComment: builder.mutation<any, CreateCompanyComment>({
                query: (payload) => {
                    return {
                        url: `/superadmin/company/${payload.CompanyId}/comments`,
                        method: 'post',
                        body: payload,
                    };
                },
                invalidatesTags: ['companyComments'],
            }),
            postCompanyApiKey: builder.mutation<any, any>({
                query: (payload) => {
                    return {
                        url: `/superadmin/apikey`,
                        method: 'post',
                        body: payload,
                    };
                },
                invalidatesTags: ['companyApiKey'],
            }),
            getIncentives: builder.query<GetIncentivesResponse, GetIncentivesQuery>({
                query: (payload) => {
                    return {
                        url: '/superadmin/incentives',
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
                providesTags: ['incentives'],
            }),
            getVossInvoices: builder.query<any, any>({
                query: (payload) => {
                    return {
                        url: '/voss/invoice',
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
                providesTags: ['vossInvoice'],
            }),
            printVossInvoices: builder.query<any, any>({
                query: (payload) => {
                    return {
                        url: '/voss/invoice/file',
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
            }),
            getVossAsyncOperations: builder.query<any, any>({
                query: (payload) => {
                    return {
                        url: '/voss/asyncOperations',
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
                providesTags: ['vossInvoice'],
            }),
            postVossInvoice: builder.mutation<any, any>({
                query: (payload) => {
                    return {
                        url: '/voss/invoice',
                        method: 'post',
                        body: payload,
                    };
                },
                invalidatesTags: ['vossInvoice'],
            }),
            getVossInvoiceBalance: builder.query<any, GetVossInvoiceBalanceQuery>({
                query: (payload) => {
                    return {
                        url: '/voss/invoice/balance',
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
                providesTags: ['vossBalance'],
            }),
            getVossUsage: builder.query<any, any>({
                query: (payload) => {
                    return {
                        url: '/voss/usage',
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
                providesTags: ['vossUsage'],
            }),
            getVossSubscriptionsDiscounts: builder.query<any, { id: string }>({
                query: ({ id }) => {
                    return {
                        url: `/voss/subscriptions/${id}/discounts`,
                        method: 'get',
                    };
                },
                providesTags: ['vossSubscriptionDiscounts'],
            }),
            getVossSubscriptionLicenses: builder.query<any, { id: string }>({
                query: ({ id }) => {
                    return {
                        url: `/voss/subscriptions/${id}/licenses`,
                        method: 'get',
                    };
                },
                providesTags: ['vossSubscriptionLicenses'],
            }),
            getVossTier: builder.query<any, any>({
                query: (payload) => {
                    return {
                        url: '/voss/tiers',
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
            }),
            getIncentiveActions: builder.query<ActionsResponse, any>({
                query: (payload) => {
                    return {
                        url: '/superadmin/incentives/action',
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
                providesTags: ['actions'],
            }),
            postActions: builder.mutation<any, any>({
                query: (payload) => {
                    return {
                        url: '/superadmin/incentives/action',
                        method: 'post',
                        body: payload,
                    };
                },
                invalidatesTags: ['actions'],
            }),
            postIncentive: builder.mutation<PostIncentiveResponse, PostIncentive>({
                query: (payload) => {
                    return {
                        url: '/superadmin/incentives',
                        method: 'post',
                        body: payload,
                    };
                },
                invalidatesTags: ['incentives'],
            }),
            putIncentive: builder.mutation<PostIncentiveResponse, PostIncentive & { Id: number }>({
                query: (payload) => {
                    return {
                        url: `/superadmin/incentives/${payload.Id}`,
                        method: 'put',
                        body: payload,
                    };
                },
                invalidatesTags: ['incentives'],
            }),
            deleteIncentive: builder.mutation<any, string>({
                query: (id) => ({
                    url: `/superadmin/incentives/${id}`,
                    method: 'delete',
                }),
                invalidatesTags: ['incentives'],
            }),
            getVossProducts: builder.query<GetVossProductsResponse, GetVossProductsQuery>({
                query: (payload) => {
                    return {
                        url: '/voss/products',
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
            }),
            getVossPlans: builder.query<any, any>({
                query: (payload) => {
                    return {
                        url: '/voss/plans',
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
            }),
            getVossProductGroups: builder.query<
                GetSingleVossProductGroupResponse,
                GetVossSingleProductGroupQuery
            >({
                query: (payload) => {
                    return {
                        url: '/voss/productgroups',
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
            }),
            getSingleVossProductGroup: builder.query<
                GetSingleVossProductGroupResponse,
                GetVossSingleProductGroupQuery
            >({
                query: ({ ProductGroupId, ...payload }) => {
                    return {
                        url: `/voss/productgroups/${ProductGroupId}`,
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
            }),
            getChargePlans: builder.query<any, any>({
                query: ({ ...payload }) => {
                    return {
                        url: `/voss/chargeplan`,
                        method: 'get',
                        // params: {
                        //     ...payload,
                        // },
                    };
                },
            }),
            postUsage: builder.mutation<any, any>({
                query: (payload) => {
                    return {
                        url: '/voss/usage',
                        method: 'post',
                        body: payload,
                    };
                },
                invalidatesTags: ['vossUsage'],
            }),
            getDiscountAgreements: builder.query<DiscountAgreementListItem[], any>({
                query: (payload) => {
                    return {
                        url: '/voss/discountagreements',
                        method: 'get',
                        params: payload,
                    };
                },
                providesTags: ['discountAgreements'],
            }),
            getDiscountAgreement: builder.query<DiscountAgreement, any>({
                query: ({ id, ...restParams }) => {
                    return {
                        url: `/voss/discountagreements/${id}`,
                        method: 'get',
                        params: restParams,
                    };
                },
                providesTags: ['discountAgreements'],
            }),
            getVossSubscriptions: builder.query<Subscription[], any>({
                query: (payload) => {
                    return {
                        url: '/voss/subscriptions',
                        method: 'get',
                        params: payload,
                    };
                },
                providesTags: ['vossSubscriptions'],
            }),

            postDiscountAgreements: builder.mutation<PostDiscountAgreement, any>({
                query: (payload) => {
                    return {
                        url: '/voss/discountagreements',
                        method: 'post',
                        body: payload,
                    };
                },
                invalidatesTags: ['discountAgreements'],
            }),
            editDiscountAgreements: builder.mutation<PutDiscontAgreementResponse, any>({
                query: (payload) => {
                    return {
                        url: '/voss/discountagreements',
                        method: 'put',
                        body: payload,
                    };
                },
                invalidatesTags: ['discountAgreements'],
            }),
            deleteDiscountAgreements: builder.mutation<{ id: string }, any>({
                query: (id) => {
                    return {
                        url: `/voss/discountagreements/${id}`,
                        method: 'delete',
                    };
                },
                invalidatesTags: ['discountAgreements'],
            }),
            addDiscountAgreementsToSubscription: builder.mutation<
                {
                    CompanyId: string;
                    CompanyOwnerId: string;
                    DiscountAgreements: {
                        DiscountAgreementId: string;
                    }[];
                },
                any
            >({
                query: (payload) => {
                    return {
                        url: '/voss/subscriptions',
                        method: 'put',
                        body: payload,
                    };
                },
                invalidatesTags: ['vossSubscriptionDiscounts'],
            }),
            getCompanyApiKey: builder.query<any, { CompanyId: string }>({
                query: ({ CompanyId }) => {
                    return {
                        url: `/superadmin/apikey/${CompanyId}`,
                        method: 'get',
                    };
                },
                providesTags: ['companyApiKey'],
            }),
            getErrorLogs: builder.query<any, any>({
                query: (params) => ({
                    url: `/errorLog`,
                    method: 'get',
                    params,
                }),
            }),
            getVossUsageAudit: builder.query<VossUsageAuditResponse, GetVossUsageAuditQuery>({
                query: (params) => ({
                    url: '/voss/usage/audit',
                    method: 'get',
                    params,
                }),
                providesTags: ['vossUsageAudit'],
            }),
            getVossLicenseAuditRuns: builder.query<
                VossLicenseAuditRunsResponse,
                GetVossLicenseAuditRunsQuery
            >({
                query: (params) => ({
                    url: '/voss/license-audit/runs',
                    method: 'get',
                    params,
                }),
                providesTags: ['vossLicenseAuditRuns'],
            }),
            getVossLicenseAuditFindings: builder.query<
                VossLicenseAuditFindingsResponse,
                GetVossLicenseAuditFindingsQuery
            >({
                query: (params) => ({
                    url: '/voss/license-audit/findings',
                    method: 'get',
                    params,
                }),
                providesTags: ['vossLicenseAuditFindings'],
            }),
            startVossLicenseAuditPreview: builder.mutation<
                StartVossLicenseAuditPreviewResponse,
                StartVossLicenseAuditPreviewPayload
            >({
                query: (body) => ({
                    url: '/voss/license-audit/runs',
                    method: 'post',
                    body,
                }),
                invalidatesTags: ['vossLicenseAuditRuns', 'vossLicenseAuditFindings'],
            }),
            cancelVossLicenseAuditRun: builder.mutation<
                CancelVossLicenseAuditRunResponse,
                { Id: number }
            >({
                query: ({ Id }) => ({
                    url: `/voss/license-audit/runs/${Id}/cancel`,
                    method: 'post',
                }),
                invalidatesTags: ['vossLicenseAuditRuns', 'vossLicenseAuditFindings'],
            }),
            getVossErrorLogs: builder.query<any, any>({
                query: (params) => ({
                    url: `/voss/errorlog`,
                    method: 'get',
                    params: {
                        OrderByDesc: 'CreatedDate',
                        ...params,
                    },
                }),
                providesTags: ['vossErrorLogs'],
            }),
            resolveVossErrorLogs: builder.mutation<
                any,
                { VossErrorLogIds: number[]; Resolved: boolean }
            >({
                query: (payload) => ({
                    url: `/voss/errorlog/resolve`,
                    method: 'put',
                    body: payload,
                }),
                invalidatesTags: ['vossErrorLogs'],
            }),
            replayVossErrorLog: builder.mutation<any, { Id: number }>({
                query: ({ Id }) => ({
                    url: `/voss/errorlog/${Id}/replay`,
                    method: 'post',
                }),
                invalidatesTags: ['vossErrorLogs'],
            }),
            repairVossCompanies: builder.mutation<
                any,
                { DryRun: boolean; CompanyIds?: string[]; PackageId?: string }
            >({
                query: (payload) => ({
                    url: `/voss/repair/companies`,
                    method: 'put',
                    body: payload,
                }),
                invalidatesTags: ['vossErrorLogs', 'vossSubscriptions'],
            }),
            peekVossDeadLetters: builder.query<any, { Queue: string; Max?: number }>({
                query: (params) => ({
                    url: `/voss/queues/deadletters`,
                    method: 'get',
                    params,
                }),
                providesTags: ['vossDeadLetters'],
            }),
            replayVossDeadLetters: builder.mutation<
                any,
                { Queue: string; Max?: number; DeadLetterReason?: string }
            >({
                query: (payload) => ({
                    url: `/voss/queues/deadletters/replay`,
                    method: 'post',
                    body: payload,
                }),
                invalidatesTags: ['vossDeadLetters', 'vossErrorLogs'],
            }),
            getLicenseChangesLog: builder.query<any, any>({
                query: (params) => ({
                    url: `/licenseChangesLog`,
                    method: 'get',
                    params,
                }),
            }),
            deleteCompanyComment: builder.mutation<any, { companyId: string; commentId: string }>({
                query: ({ companyId, commentId }) => {
                    return {
                        url: `/superadmin/company/${companyId}/comments/${commentId}`,
                        method: 'delete',
                    };
                },
                invalidatesTags: ['companyComments'],
            }),
            getScheduledChanges: builder.query<any, any>({
                query: ({ ...payload }) => {
                    return {
                        url: `/voss/subscriptions/scheduledChanges`,
                        method: 'get',
                        params: {
                            ...payload,
                        },
                    };
                },
            }),
            getMailchimpSyncExecutions: builder.query<MailchimpSyncExecution[], MailchimpSyncExecutionsQuery>({
                query: (params) => ({
                    url: '/mailchimp/syncExecutions',
                    method: 'get',
                    params,
                }),
                providesTags: ['mailchimpSyncExecutions'],
            }),
            getWidgetConfigurationSchemas: builder.query<
                GetWidgetConfigurationSchemasResponse,
                WidgetConfigurationSchemaQuery
            >({
                query: (params) => ({
                    url: '/widget/configurationschema',
                    method: 'get',
                    params,
                }),
                transformResponse: (res: definitions['QueryResponse_WidgetConfigurationSchemaResponse_']) => {
                    const results = res.Results ?? [];
                    return {
                        ...res,
                        Results: results.map((result) => {
                            if (!result?.Schema) return result;
                            const schema = { ...result.Schema } as any;
                            if (typeof schema.properties === 'string') schema.properties = JSON.parse(schema.properties);
                            if (typeof schema.$defs === 'string') schema.$defs = JSON.parse(schema.$defs);
                            if (typeof schema.required === 'string') schema.required = JSON.parse(schema.required);
                            return { ...result, Schema: schema };
                        }),
                    };
                },
                providesTags: ['widgetConfigurationSchemas'],
            }),
            postWidgetConfigurationSchema: builder.mutation<
                WidgetConfigurationSchemaResponse,
                CreateWidgetConfigurationSchemaBody
            >({
                query: (body) => ({
                    url: '/widget/configurationschema',
                    method: 'post',
                    body,
                }),
                invalidatesTags: ['widgetConfigurationSchemas'],
            }),
            getWidgetConfigurations: builder.query<GetWidgetConfigurationsResponse, GetWidgetConfigurationsQuery>({
                query: (params) => ({
                    url: '/widget/configuration',
                    method: 'get',
                    params,
                }),
                providesTags: ['widgetConfigurations'],
            }),
            getWidgetConfigurationById: builder.query<WidgetConfigurationResponse, WidgetConfigurationByIdParams>({
                query: ({ Id, ...params }) => ({
                    url: `/widget/configuration/${Id}`,
                    method: 'get',
                    params,
                }),
                providesTags: ['widgetConfigurations'],
            }),
            postWidgetConfiguration: builder.mutation<WidgetConfigurationResponse, CreateWidgetConfigurationBody>({
                query: (body) => ({
                    url: '/widget/configuration',
                    method: 'post',
                    body,
                }),
                invalidatesTags: ['widgetConfigurations'],
            }),
            putWidgetConfiguration: builder.mutation<WidgetConfigurationResponse, UpdateWidgetConfigurationBody>({
                query: ({ Id, ...body }) => ({
                    url: `/widget/configuration/${Id}`,
                    method: 'put',
                    body,
                }),
                invalidatesTags: ['widgetConfigurations'],
            }),
            deleteWidgetConfiguration: builder.mutation<WidgetConfigurationResponse, DeleteWidgetConfigurationParams>({
                query: ({ Id, ...params }) => ({
                    url: `/widget/configuration/${Id}`,
                    method: 'delete',
                    params,
                }),
                invalidatesTags: ['widgetConfigurations'],
            }),
            resetWidgetConfiguration: builder.mutation<WidgetConfigurationResponse, ResetWidgetConfigurationParams>({
                query: ({ Id, ...params }) => ({
                    url: `/widget/configuration/${Id}/reset`,
                    method: 'put',
                    params,
                }),
                invalidatesTags: ['widgetConfigurations'],
            }),
            bumpWidgetConfigurationVersion: builder.mutation<WidgetConfigurationResponse, BumpWidgetConfigurationVersionBody>({
                query: ({ Id, ...body }) => ({
                    url: `/widget/configuration/${Id}/bumpversion`,
                    method: 'put',
                    body,
                }),
                invalidatesTags: ['widgetConfigurations'],
            }),
            setWidgetConfigurationSchemaMigration: builder.mutation<
                WidgetConfigurationSchemaResponse,
                SetWidgetConfigurationSchemaMigrationBody
            >({
                query: ({ Version, ...body }) => ({
                    url: `/widget/configurationschema/${Version}/migration`,
                    method: 'put',
                    body,
                }),
                invalidatesTags: ['widgetConfigurationSchemas'],
            }),
            deleteWidgetConfigurationSchemaMigration: builder.mutation<
                WidgetConfigurationSchemaResponse,
                DeleteWidgetConfigurationSchemaMigrationParams
            >({
                query: ({ Version }) => ({
                    url: `/widget/configurationschema/${Version}/migration`,
                    method: 'delete',
                }),
                invalidatesTags: ['widgetConfigurationSchemas'],
            }),
            getAssistantUsage: builder.query<GetAssistantUsageResponse, GetAssistantUsageQuery>({
                query: (params) => ({
                    url: '/superadmin/assistant/usage',
                    method: 'get',
                    params,
                }),
                providesTags: ['assistantUsage'],
            }),
            getFeatureFlags: builder.query<GetFeatureFlagsResponse, GetFeatureFlagsQuery>({
                query: (params) => ({
                    url: '/superadmin/feature',
                    method: 'get',
                    params,
                }),
                providesTags: ['featureFlags'],
            }),
            postFeatureFlag: builder.mutation<FeatureFlagAdminResponse, CreateFeatureFlagBody>({
                query: (body) => ({
                    url: '/superadmin/feature',
                    method: 'post',
                    body,
                }),
                invalidatesTags: ['featureFlags'],
            }),
            putFeatureFlag: builder.mutation<FeatureFlagAdminResponse, UpdateFeatureFlagBody>({
                query: ({ Id, ...body }) => ({
                    url: `/superadmin/feature/${Id}`,
                    method: 'put',
                    body,
                }),
                invalidatesTags: ['featureFlags'],
            }),
            deleteFeatureFlag: builder.mutation<void, DeleteFeatureFlagParams>({
                query: ({ Id }) => ({
                    url: `/superadmin/feature/${Id}`,
                    method: 'delete',
                }),
                invalidatesTags: ['featureFlags'],
            }),
            getCompanyFeatureFlags: builder.query<GetCompanyFeatureFlagsResponse, { CompanyId: string }>({
                query: ({ CompanyId }) => ({
                    url: `/feature/${CompanyId}`,
                    method: 'get',
                }),
                providesTags: (_res, _err, { CompanyId }) => [{ type: 'companyFeatureFlags', id: CompanyId }],
            }),
            setCompanyFeatureFlag: builder.mutation<CompanyFeatureFlagResponse, SetCompanyFeatureFlagBody>({
                query: ({ CompanyId, Id, IsEnabled }) => ({
                    url: `/feature/${CompanyId}/${Id}`,
                    method: 'put',
                    body: { IsEnabled },
                }),
                invalidatesTags: (_res, _err, { CompanyId }) => [{ type: 'companyFeatureFlags', id: CompanyId }],
            }),
            bulkSetCompanyFeatureFlag: builder.mutation<BulkSetCompanyFeatureFlagResponse, BulkSetCompanyFeatureFlagBody>({
                query: ({ Id, Overrides }) => ({
                    url: `/superadmin/feature/${Id}/companies`,
                    method: 'put',
                    body: { Overrides },
                }),
                invalidatesTags: (_res, _err, { Overrides }) =>
                    Overrides.map(({ CompanyId }) => ({ type: 'companyFeatureFlags' as const, id: CompanyId })),
            }),
            getRedirectDomains: builder.query<GetRedirectDomainsResponse, GetRedirectDomainsQuery>({
                query: (params) => ({
                    url: '/superadmin/redirect-domains',
                    method: 'get',
                    params,
                }),
                providesTags: ['redirectDomains'],
            }),
            postRedirectDomain: builder.mutation<
                AllowedRedirectDomainResponse,
                CreateRedirectDomainBody
            >({
                query: (body) => ({
                    url: '/superadmin/redirect-domains',
                    method: 'post',
                    body,
                }),
                invalidatesTags: ['redirectDomains'],
            }),
            deleteRedirectDomain: builder.mutation<void, DeleteRedirectDomainParams>({
                query: ({ Id }) => ({
                    url: `/superadmin/redirect-domains/${Id}`,
                    method: 'delete',
                }),
                invalidatesTags: ['redirectDomains'],
            }),
            getTickets: builder.query<TicketsResponse, GetTicketsQuery>({
                query: (params) => ({
                    url: '/superadmin/support/cases',
                    method: 'get',
                    params: {
                        IncludeCaseStatusInformation: true,
                        IncludeCaseTypeInformation: true,
                        IncludeCaseAreaInformation: true,
                        ...params,
                    },
                }),
                providesTags: ['tickets'],
            }),
            getTicket: builder.query<Ticket | undefined, { Id: number; CompanyId?: string }>({
                query: ({ Id, CompanyId }) => ({
                    url: '/superadmin/support/cases',
                    method: 'get',
                    params: {
                        Id,
                        ...(CompanyId ? { CompanyId } : {}),
                        IncludeCaseComments: true,
                        IncludeCaseAttachments: true,
                        IncludeCaseStatusInformation: true,
                        IncludeCaseTypeInformation: true,
                        IncludeCaseAreaInformation: true,
                    },
                }),
                transformResponse: (response: TicketsResponse) => response.Results?.[0],
                providesTags: (res, _err, arg) => [{ type: 'tickets', id: arg.Id }],
            }),
            getTicketStatuses: builder.query<TicketStatusesResponse, void>({
                query: () => ({
                    url: '/superadmin/support/cases/status',
                    method: 'get',
                }),
                providesTags: ['ticketLookups'],
            }),
            getTicketTypes: builder.query<TicketTypesResponse, void>({
                query: () => ({
                    url: '/superadmin/support/cases/types',
                    method: 'get',
                }),
                providesTags: ['ticketLookups'],
            }),
            getTicketAreas: builder.query<TicketAreasResponse, void>({
                query: () => ({
                    url: '/superadmin/support/cases/areas',
                    method: 'get',
                }),
                providesTags: ['ticketLookups'],
            }),
            postTicket: builder.mutation<Ticket, CreateTicket>({
                query: (payload) => ({
                    url: '/superadmin/support/cases',
                    method: 'post',
                    body: payload,
                }),
                invalidatesTags: ['tickets'],
            }),
            putTicket: builder.mutation<Ticket, UpdateTicket>({
                query: (payload) => ({
                    url: `/superadmin/support/cases/${payload.Id}`,
                    method: 'put',
                    body: payload,
                }),
                invalidatesTags: (_res, _err, arg) => ['tickets', { type: 'tickets', id: arg.Id }],
            }),
            deleteTicket: builder.mutation<Ticket, DeleteTicketParams>({
                query: ({ Id, CompanyId }) => ({
                    url: `/superadmin/support/cases/${Id}`,
                    method: 'delete',
                    params: CompanyId ? { CompanyId } : undefined,
                }),
                invalidatesTags: ['tickets'],
            }),
            getTicketComments: builder.query<
                TicketCommentsResponse,
                { SupportCaseId: number; CompanyId?: string }
            >({
                query: ({ SupportCaseId, CompanyId }) => ({
                    url: `/superadmin/support/cases/${SupportCaseId}/comments`,
                    method: 'get',
                    params: CompanyId ? { CompanyId, OrderByDesc: 'Created' } : { OrderByDesc: 'Created' },
                }),
                providesTags: (_res, _err, arg) => [{ type: 'ticketComments', id: arg.SupportCaseId }],
            }),
            postTicketComment: builder.mutation<TicketComment, CreateTicketComment>({
                query: (payload) => ({
                    url: `/superadmin/support/cases/${payload.Id}/comments`,
                    method: 'post',
                    body: payload,
                }),
                invalidatesTags: (_res, _err, arg) => [
                    { type: 'ticketComments', id: arg.Id },
                    { type: 'tickets', id: arg.Id },
                ],
            }),
            deleteTicketComment: builder.mutation<TicketComment, DeleteTicketCommentParams>({
                query: ({ SupportCaseId, Id, CompanyId }) => ({
                    url: `/superadmin/support/cases/${SupportCaseId}/comments/${Id}`,
                    method: 'delete',
                    params: CompanyId ? { CompanyId } : undefined,
                }),
                invalidatesTags: (_res, _err, arg) => [
                    { type: 'ticketComments', id: arg.SupportCaseId },
                    { type: 'tickets', id: arg.SupportCaseId },
                ],
            }),
            getBillingInformation: builder.query<BillingInformation, { CompanyId: string }>({
                query: ({ CompanyId }) => ({
                    url: '/superadmin/billing/company/billinginformation',
                    method: 'get',
                    params: { CompanyId },
                }),
                providesTags: (_res, _err, arg) => [
                    { type: 'billingInformation', id: arg.CompanyId },
                ],
            }),
            putBillingInformation: builder.mutation<
                BillingInformation,
                UpdateBillingInformationPayload
            >({
                query: (payload) => ({
                    url: '/superadmin/billing/company/billinginformation',
                    method: 'put',
                    body: payload,
                }),
                invalidatesTags: (_res, _err, arg) => [
                    { type: 'billingInformation', id: arg.CompanyId },
                ],
            }),
            getCompanyServices: builder.query<CompanyServicesResponse, GetCompanyServicesQuery>({
                query: (params) => ({
                    url: '/services',
                    method: 'get',
                    params,
                }),
                providesTags: (_res, _err, arg) => [{ type: 'companyServices', id: arg.CompanyId }],
            }),
            getCompanyServicePrices: builder.query<
                CompanyServicePricesResponse,
                GetCompanyServicePricesQuery
            >({
                query: (params) => ({
                    url: '/services/prices',
                    method: 'get',
                    params,
                }),
                providesTags: (_res, _err, arg) => [
                    { type: 'companyServicePrices', id: arg.CompanyId },
                ],
            }),
            getCompanyArticles: builder.query<CompanyArticlesResponse, GetCompanyArticlesQuery>({
                query: (params) => ({
                    url: '/articles',
                    method: 'get',
                    params,
                }),
                providesTags: (_res, _err, arg) => [{ type: 'companyArticles', id: arg.CompanyId }],
            }),
            getArticleTypes: builder.query<ArticleTypesResponse, void>({
                query: () => ({
                    url: '/articles/types',
                    method: 'get',
                }),
            }),
            getBillingMethods: builder.query<QueryResults<BillingMethodOption>, void>({
                query: () => ({
                    url: '/billing/methods',
                    method: 'get',
                    params: { Take: 100 },
                }),
                // /billing/methods returns a bare array, normalise it to the query shape
                transformResponse: (response: BillingMethodOption[] | QueryResults<BillingMethodOption>) =>
                    Array.isArray(response) ? { Results: response } : response,
            }),
            getBillingCountries: builder.query<QueryResults<BillingCountryOption>, void>({
                query: () => ({
                    url: '/countries',
                    method: 'get',
                    params: { Take: 500 },
                }),
            }),
            getBillingLicensePlans: builder.query<QueryResults<BillingLicensePlanOption>, void>({
                query: () => ({
                    url: '/licenses/plans/',
                    method: 'get',
                    params: { Take: 50 },
                }),
            }),
            getCustomers: builder.query<CustomersResponse, GetCustomersQuery>({
                query: (params) => ({
                    url: '/superadmin/customers',
                    method: 'get',
                    params: {
                        Take: 50,
                        ...params,
                    },
                }),
                providesTags: ['customers'],
            }),
            deleteCustomer: builder.mutation<void, DeleteCustomerParams>({
                query: ({ Id, CompanyId, DeleteUserProfileAndIdentity }) => ({
                    url: `/superadmin/customers/${Id}`,
                    method: 'delete',
                    params: {
                        CompanyId,
                        ...(DeleteUserProfileAndIdentity === undefined
                            ? {}
                            : { DeleteUserProfileAndIdentity }),
                    },
                }),
                invalidatesTags: ['customers'],
            }),
        };
    },
});

export const {
    useGetCompanyQuery,
    useLazyGetCompanyQuery,
    useGetCompaniesQuery,
    useLazyGetCompaniesQuery,
    useGetCompanyOwnersQuery,
    useLazyGetCompanyOwnersQuery,
    usePutCompanyMutation,
    useGetCompanyTypesQuery,
    useGetCompanyCategoriesQuery,
    useGetCountriesQuery,
    useGetCompanyBookingsQuery,
    useLazyGetCompanyBookingsQuery,
    useGetCompanyAdministratorsQuery,
    useLazyGetCompanyAdministratorsQuery,
    useGetCompanyLicensesQuery,
    useLazyGetCompanyLicensesQuery,
    useGetLicenseTypesQuery,
    useLazyGetLicenseTypesQuery,
    useGetCompanyInvoicesQuery,
    useLazyGetCompanyInvoicesQuery,
    useGetCompanyCommentsQuery,
    useLazyGetCompanyCommentsQuery,
    useGetCommentTypesQuery,
    useLazyGetCommentTypesQuery,
    useGetCompanyApiKeyQuery,
    useLazyGetCompanyApiKeyQuery,
    useGetIncentivesQuery,
    useLazyGetIncentivesQuery,
    useGetIncentiveActionsQuery,
    useLazyGetIncentiveActionsQuery,
    useGetVossInvoicesQuery,
    useLazyGetVossInvoicesQuery,
    usePrintVossInvoicesQuery,
    useLazyPrintVossInvoicesQuery,
    useGetVossAsyncOperationsQuery,
    useLazyGetVossAsyncOperationsQuery,
    useGetVossInvoiceBalanceQuery,
    useLazyGetVossInvoiceBalanceQuery,
    useGetVossUsageQuery,
    useLazyGetVossUsageQuery,
    useGetVossSubscriptionsDiscountsQuery,
    useGetVossSubscriptionLicensesQuery,
    useLazyGetVossSubscriptionsDiscountsQuery,
    useGetVossTierQuery,
    useLazyGetVossTierQuery,
    useGetVossProductsQuery,
    useLazyGetVossProductsQuery,
    useGetVossPlansQuery,
    useLazyGetVossPlansQuery,
    useGetVossProductGroupsQuery,
    useLazyGetVossProductGroupsQuery,
    useGetSingleVossProductGroupQuery,
    useLazyGetSingleVossProductGroupQuery,
    useGetChargePlansQuery,
    useLazyGetChargePlansQuery,
    useGetDiscountAgreementsQuery,
    useLazyGetDiscountAgreementsQuery,
    useGetDiscountAgreementQuery,
    useLazyGetDiscountAgreementQuery,
    useGetVossSubscriptionsQuery,
    useLazyGetVossSubscriptionsQuery,
    useGetScheduledChangesQuery,
    useLazyGetScheduledChangesQuery,
    useGetErrorLogsQuery,
    useLazyGetErrorLogsQuery,
    useGetVossErrorLogsQuery,
    useLazyGetVossErrorLogsQuery,
    useLazyGetVossUsageAuditQuery,
    useGetVossLicenseAuditRunsQuery,
    useGetVossLicenseAuditFindingsQuery,
    useStartVossLicenseAuditPreviewMutation,
    useCancelVossLicenseAuditRunMutation,
    useResolveVossErrorLogsMutation,
    useReplayVossErrorLogMutation,
    useRepairVossCompaniesMutation,
    useLazyPeekVossDeadLettersQuery,
    useReplayVossDeadLettersMutation,
    useGetLicenseChangesLogQuery,
    useLazyGetLicenseChangesLogQuery,
    useGetMailchimpSyncExecutionsQuery,
    useLazyGetMailchimpSyncExecutionsQuery,
    useGetWidgetConfigurationSchemasQuery,
    useLazyGetWidgetConfigurationSchemasQuery,
    usePostWidgetConfigurationSchemaMutation,
    useGetWidgetConfigurationsQuery,
    useLazyGetWidgetConfigurationsQuery,
    useGetWidgetConfigurationByIdQuery,
    useLazyGetWidgetConfigurationByIdQuery,
    usePostWidgetConfigurationMutation,
    usePutWidgetConfigurationMutation,
    useDeleteWidgetConfigurationMutation,
    useResetWidgetConfigurationMutation,
    useBumpWidgetConfigurationVersionMutation,
    useSetWidgetConfigurationSchemaMigrationMutation,
    useDeleteWidgetConfigurationSchemaMigrationMutation,
    useGetAssistantUsageQuery,
    useGetFeatureFlagsQuery,
    useLazyGetFeatureFlagsQuery,
    usePostFeatureFlagMutation,
    usePutFeatureFlagMutation,
    useDeleteFeatureFlagMutation,
    useGetCompanyFeatureFlagsQuery,
    useLazyGetCompanyFeatureFlagsQuery,
    useSetCompanyFeatureFlagMutation,
    useBulkSetCompanyFeatureFlagMutation,
    useGetRedirectDomainsQuery,
    useLazyGetRedirectDomainsQuery,
    usePostRedirectDomainMutation,
    useDeleteRedirectDomainMutation,
    useGetTicketsQuery,
    useLazyGetTicketsQuery,
    useGetTicketQuery,
    useLazyGetTicketQuery,
    useGetTicketStatusesQuery,
    useLazyGetTicketStatusesQuery,
    useGetTicketTypesQuery,
    useLazyGetTicketTypesQuery,
    useGetTicketAreasQuery,
    useLazyGetTicketAreasQuery,
    usePostTicketMutation,
    usePutTicketMutation,
    useDeleteTicketMutation,
    useGetTicketCommentsQuery,
    useLazyGetTicketCommentsQuery,
    usePostTicketCommentMutation,
    useDeleteTicketCommentMutation,
    useGetBillingInformationQuery,
    usePutBillingInformationMutation,
    useGetCompanyServicesQuery,
    useGetCompanyServicePricesQuery,
    useGetCompanyArticlesQuery,
    useGetArticleTypesQuery,
    useGetBillingMethodsQuery,
    useGetBillingCountriesQuery,
    useGetBillingLicensePlansQuery,
    useGetCustomersQuery,
    useLazyGetCustomersQuery,
    useDeleteCustomerMutation,
    useDeleteCompanyMutation,
    useGetCompanyDeletionSchedulesQuery,
    useScheduleCompanyDeletionMutation,
    useCancelCompanyDeletionScheduleMutation,
    useApproveCompanyMutation,
    useEndCompanyLicensesMutation,
    useSendNewsletterBroadcastMutation,
    useTestSendNewsletterBroadcastMutation,
    useSyncMailchimpUsersMutation,
    useResetDemoAccountsMutation,
    usePostCompanyCommentMutation,
    useDeleteCompanyCommentMutation,
    usePostCompanyApiKeyMutation,
    usePostIncentiveMutation,
    usePutIncentiveMutation,
    useDeleteIncentiveMutation,
    usePostActionsMutation,
    usePostVossInvoiceMutation,
    usePostUsageMutation,
    usePostDiscountAgreementsMutation,
    useEditDiscountAgreementsMutation,
    useDeleteDiscountAgreementsMutation,
    useAddDiscountAgreementsToSubscriptionMutation,
} = apiService;
